import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["src"]
const _hoisted_2 = { class: "personal-information-editing-drawer__close-trigger" }
const _hoisted_3 = { class: "personal-information-editing-drawer__section" }
const _hoisted_4 = { class: "personal-information-editing-drawer__section--section-content" }
const _hoisted_5 = { class: "personal-information-editing-drawer__section" }
const _hoisted_6 = { class: "personal-information-editing-drawer__section--section-content" }
const _hoisted_7 = ["disabled"]

import { computed, nextTick, reactive, ref } from 'vue';
import { CloseOutlined } from '@ant-design/icons-vue';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import type { FormInstance, Rule } from 'ant-design-vue/es/form';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { ICONS_BASE, useEditingDrawer } from './composables/use-editing-drawer';

type PersonalFormResponse = {
  status: 'success' | 'error';
  msg:
    | {
        id?: string | number;
        name?: string;
        last_name?: string;
        second_name?: string;
        work_position?: string;
        email?: string;
        phone?: string;
      }
    | string;
};

type PersonalFormState = ReturnType<typeof initialFormState>;


export default /*@__PURE__*/_defineComponent({
  __name: 'personal-information-editing-drawer',
  setup(__props) {

const { open, showDrawer, onClose: closeDrawer } = useEditingDrawer();

const drawerFormRef = ref<FormInstance>();
const isSubmitting = ref(false);
const isLoading = ref(false);
const PERSONAL_FORM_URL = getApiUrl('/api/form/personal/');

const initialFormState = () => ({
  last_name: '',
  first_name: '',
  middle_name: '',
  position: '',
  phone: '',
  email: '',
});

const form = reactive(initialFormState());
const savedFormState = ref<PersonalFormState | null>(null);

const isNotBlankString = (value: string): boolean => value.trim().length > 0;
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const isRequiredFieldsFilled = computed(
  () => isNotBlankString(form.phone) && isNotBlankString(form.email),
);

function captureFormState(): PersonalFormState {
  return {
    last_name: form.last_name,
    first_name: form.first_name,
    middle_name: form.middle_name,
    position: form.position,
    phone: form.phone,
    email: form.email,
  };
}

function isSameFormState(current: PersonalFormState, initial: PersonalFormState): boolean {
  return (
    current.last_name === initial.last_name &&
    current.first_name === initial.first_name &&
    current.middle_name === initial.middle_name &&
    current.position === initial.position &&
    current.phone === initial.phone &&
    current.email === initial.email
  );
}

const hasUnsavedChanges = computed(() => {
  const initial = savedFormState.value;
  if (!initial) return false;

  return !isSameFormState(captureFormState(), initial);
});

const isSaveDisabled = computed(
  () =>
    isLoading.value ||
    isSubmitting.value ||
    !isRequiredFieldsFilled.value ||
    !isValidEmail(form.email) ||
    !hasUnsavedChanges.value,
);

const rules: Record<string, Rule[]> = {
  last_name: [{ message: 'Введите фамилию', whitespace: true }],
  first_name: [{ message: 'Введите имя', whitespace: true }],
  middle_name: [{ message: 'Введите отчество', whitespace: true }],
  position: [{ message: 'Введите должность', whitespace: true }],
  phone: [{ required: true, message: 'Введите телефон', whitespace: true }],
  email: [{ required: true, message: 'Введите e-mail', type: 'email' }],
};

const resetFormState = () => {
  Object.assign(form, initialFormState());
  savedFormState.value = null;
  nextTick(() => {
    drawerFormRef.value?.clearValidate();
  });
};

const applyPersonalData = (data: PersonalFormResponse['msg']) => {
  if (!data || typeof data === 'string') return;

  form.last_name = data.last_name ?? '';
  form.first_name = data.name ?? '';
  form.middle_name = data.second_name ?? '';
  form.position = data.work_position ?? '';
  form.email = data.email ?? '';
  form.phone = data.phone ?? '';
};

function setPersonalFieldValue(label: string, value: string): void {
  const sections = document.querySelectorAll<HTMLElement>('.general-information__section');

  sections.forEach((section) => {
    const title = section.querySelector('.general-information__section-title');
    if (title?.textContent?.trim() !== 'Контактное лицо') return;

    section.querySelectorAll<HTMLElement>('.general-information__field').forEach((field) => {
      const labelEl = field.querySelector('.general-information__field-label');
      const valueEl = field.querySelector('.general-information__field-value');

      if (!labelEl || !valueEl) return;
      if (labelEl.textContent?.trim() !== label) return;

      valueEl.textContent = value;
    });
  });
}

function syncPersonalDataToPage(): void {
  const fullName = [form.last_name, form.first_name, form.middle_name].filter(Boolean).join(' ');

  setPersonalFieldValue('ФИО', fullName);
  setPersonalFieldValue('Должность', form.position);
  setPersonalFieldValue('Телефон', form.phone);
  setPersonalFieldValue('Email', form.email);

  const sidebarEmail = document.querySelector('.residency-lk-sidebar__desc');
  if (sidebarEmail) {
    sidebarEmail.textContent = form.email;
  }
}

const loadPersonalData = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(PERSONAL_FORM_URL, {
      method: 'GET',
      credentials: 'same-origin',
    });
    const result = (await response.json()) as PersonalFormResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось загрузить личные данные',
      );
    }

    applyPersonalData(result.msg);
    savedFormState.value = captureFormState();
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const openDrawer = async () => {
  showDrawer();
  await loadPersonalData();
};

const onSubmit = async () => {
  if (!drawerFormRef.value) return;

  try {
    await drawerFormRef.value.validate();
  } catch {
    return;
  }

  isSubmitting.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const body = new FormData();
    body.append('last_name', form.last_name);
    body.append('first_name', form.first_name);
    body.append('middle_name', form.middle_name);
    body.append('position', form.position);
    body.append('email', form.email);
    body.append('phone', form.phone);
    body.append('sessid', sessid);

    const response = await fetch(PERSONAL_FORM_URL, {
      method: 'POST',
      credentials: 'same-origin',
      body,
      headers: {
        'X-Bitrix-Csrf-Token': sessid,
      },
    });
    const result = (await response.json()) as PersonalFormResponse;

    if (!response.ok || result.status !== 'success') {
      throw new Error(
        typeof result.msg === 'string' ? result.msg : 'Не удалось сохранить личные данные',
      );
    }

    savedFormState.value = captureFormState();
    syncPersonalDataToPage();
    showStcSuccessNotification(typeof result.msg === 'string' ? result.msg : 'Данные успешно сохранены');
    closeDrawer();
  } catch (error) {
    showStcErrorNotification(error instanceof Error ? error.message : 'Ошибка соединения');
  } finally {
    isSubmitting.value = false;
  }
};

const handleClose = () => {
  closeDrawer();
  resetFormState();
};

return (_ctx: any,_cache: any) => {
  const _component_a_tooltip = _resolveComponent("a-tooltip")!
  const _component_a_divider = _resolveComponent("a-divider")!
  const _component_a_input = _resolveComponent("a-input")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_col = _resolveComponent("a-col")!
  const _component_a_row = _resolveComponent("a-row")!
  const _component_a_drawer = _resolveComponent("a-drawer")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_a_tooltip, {
      title: "Редактировать",
      placement: "bottom",
      "overlay-class-name": "general-information-editing-tooltip"
    }, {
      default: _withCtx(() => [
        _createElementVNode("button", {
          type: "button",
          class: "general-information__personal-information-editing-btn",
          "aria-label": "Редактировать личные данные",
          onClick: openDrawer
        }, [
          _createElementVNode("img", {
            src: `${_unref(ICONS_BASE)}/edit.svg`,
            alt: "",
            class: "general-information__personal-information-editing-icon"
          }, null, 8, _hoisted_1)
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_a_drawer, {
      width: 800,
      open: _unref(open),
      class: "personal-information-editing-drawer",
      "body-style": { padding: '0px 40px 32px 40px' },
      "footer-style": { textAlign: 'left', padding: '20px 40px 40px 40px' },
      "header-style": { padding: '32px 40px', border: 'none' },
      onClose: handleClose
    }, {
      title: _withCtx(() => [...(_cache[6] || (_cache[6] = [
        _createElementVNode("span", { class: "personal-information-editing-drawer__title" }, "Личные данные", -1)
      ]))]),
      closeIcon: _withCtx(() => [
        _createElementVNode("span", _hoisted_2, [
          _createVNode(_unref(CloseOutlined))
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_component_a_row, {
          align: "middle",
          justify: "end",
          class: "personal-information-editing-drawer__toolbar"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_col, null, {
              default: _withCtx(() => [
                _createVNode(_component_a_space, null, {
                  default: _withCtx(() => [
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-outline btn-medium",
                      onClick: handleClose
                    }, " Отмена "),
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn btn-primary btn-medium",
                      disabled: isSaveDisabled.value,
                      onClick: onSubmit
                    }, " Сохранить ", 8, _hoisted_7)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createVNode(_component_a_divider, { class: "personal-information-editing-drawer__divider-header" }),
        _createVNode(_component_a_form, {
          ref_key: "drawerFormRef",
          ref: drawerFormRef,
          class: "personal-information-editing-drawer__form",
          model: form,
          rules: rules,
          layout: "vertical"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_3, [
              _cache[7] || (_cache[7] = _createElementVNode("span", { class: "personal-information-editing-drawer__section--section-title" }, "Общее", -1)),
              _createElementVNode("div", _hoisted_4, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Фамилия",
                  name: "last_name"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.last_name,
                      "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.last_name) = $event)),
                      placeholder: "Полное название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Имя",
                  name: "first_name"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.first_name,
                      "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.first_name) = $event)),
                      placeholder: "Полное название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Отчество",
                  name: "middle_name"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.middle_name,
                      "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.middle_name) = $event)),
                      placeholder: "Полное название"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Должность",
                  name: "position"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.position,
                      "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.position) = $event)),
                      placeholder: "Название должности"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ]),
            _createVNode(_component_a_divider, { class: "personal-information-editing-drawer__divider" }),
            _createElementVNode("div", _hoisted_5, [
              _cache[8] || (_cache[8] = _createElementVNode("span", { class: "personal-information-editing-drawer__section--section-title" }, "Контакты", -1)),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Телефон",
                  name: "phone",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.phone,
                      "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.phone) = $event)),
                      placeholder: "+7 (___) ___-__-__"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_a_form_item, {
                  class: "stc-ant-field-skin",
                  label: "Email",
                  name: "email",
                  required: ""
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_a_input, {
                      value: form.email,
                      "onUpdate:value": _cache[5] || (_cache[5] = ($event: any) => ((form.email) = $event)),
                      placeholder: "user@example.com"
                    }, null, 8, ["value"])
                  ]),
                  _: 1
                })
              ])
            ])
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})